'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  PRODUCT_LINKS,
  INDUSTRY_LINKS,
  COMPANY_LINKS,
} from '../lib/site'

type NavLink = { href: string; label: string }

/* Firms is temporarily hidden from the nav. The link data stays in
   site.ts; we just filter it out of the rendered menus for now.
   Docs lives in the Company dropdown (COMPANY_LINKS in site.ts),
   not here. */
const NAV_PRODUCT_LINKS = PRODUCT_LINKS.filter(
  (link) => link.href !== '/firms'
)

/* Money Transmitter is hidden from the nav dropdown. Its page and
   link data stay; this only trims the rendered menu. Collections
   (the /debt-collection page) is its own entry beside Lending. */
const NAV_INDUSTRY_LINKS = INDUSTRY_LINKS.filter(
  (link) => link.href !== '/money-transmitter'
)

/* An entry with `links` renders as a hover dropdown; one with `href`
   renders as a plain link (Security is its own page, not a menu). */
type NavEntry =
  | { label: string; links: readonly NavLink[] }
  | { label: string; href: string }

/* The top-level "Reference" dropdown (the licensing-directory tables) was
   removed from the header (owner's call, 2026-09): the connector docs now
   surface via "Docs" under Company, and the licensing-reference tables keep
   their route in from the footer's Reference column, which already lists
   them. An "API reference" link was added alongside them there. */
const NAV_ENTRIES: NavEntry[] = [
  { label: 'Industries', links: NAV_INDUSTRY_LINKS },
  { label: 'Product', links: NAV_PRODUCT_LINKS },
  { label: 'Company', links: COMPANY_LINKS },
  { label: 'Security', href: '/security' },
]

/* Product routes map to their accent scope so dropdown hovers sweep
   in each product's own color (via the global [data-product] rules). */
const PRODUCT_ACCENTS: Record<string, string> = {
  '/apply': 'apply',
  '/maintain': 'maintain',
  '/seats': 'seats',
  '/api': 'api',
}

/* mailto and external links render as plain anchors;
   everything else goes through next/link */
function NavItem({
  link,
  className,
  onClick,
}: {
  link: NavLink
  className: string
  onClick?: () => void
}) {
  const accent = PRODUCT_ACCENTS[link.href]
  if (link.href.startsWith('mailto:') || link.href.startsWith('http')) {
    return (
      <a href={link.href} className={className} onClick={onClick}>
        {link.label}
      </a>
    )
  }
  return (
    <Link
      href={link.href}
      className={className}
      onClick={onClick}
      data-product={accent}
    >
      {link.label}
    </Link>
  )
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* Close the mobile menu after any navigation */
  const closeMenu = () => setMenuOpen(false)

  /* Dropdowns open on hover AND on focus (:focus-within, for keyboard
     users). After a click the trigger keeps focus, so hovering a
     different entry would show two menus at once. Blur the stale
     trigger the moment the mouse moves anywhere outside its dropdown. */
  const closeStaleDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return
    const activeDropdown = active.closest('.navbar-dropdown')
    if (activeDropdown && !activeDropdown.contains(e.target as Node)) {
      active.blur()
    }
  }

  return (
    <>
      <a
        className={`announce-bar${scrolled ? ' hidden' : ''}`}
        href="https://www.ycombinator.com/companies/payna"
        target="_blank"
        rel="noopener noreferrer"
      >
        Payna is backed by{' '}
        <span className="announce-bar-yc">
          <Image
            src="/yc-logo.png"
            alt=""
            width={16}
            height={16}
            className="announce-bar-yc-logo"
          />
          Y Combinator
        </span>
      </a>
      <nav className={`navbar${scrolled || menuOpen ? ' scrolled' : ''}`}>
      <div className="navbar-inner">
        <Link href="/" className="navbar-brand" onClick={closeMenu}>
          <Image
            src="/logo.svg"
            alt="Payna logo"
            width={167}
            height={171}
            className="navbar-logo"
            priority
          />
          <span className="navbar-brand-name">Payna</span>
        </Link>

        {/* Centered against the whole bar (absolute, so the uneven
            brand/buttons widths on either side can't push it off).
            Dropdowns open on hover and on keyboard focus. */}
        <div className="navbar-links" onMouseOver={closeStaleDropdown}>
          {NAV_ENTRIES.map((entry) =>
            'links' in entry ? (
              <div key={entry.label} className="navbar-dropdown">
                <button type="button" className="navbar-link navbar-dropdown-trigger">
                  {entry.label}
                </button>
                <div className="navbar-dropdown-menu">
                  {entry.links.map((link) => (
                    <NavItem
                      key={link.href}
                      link={link}
                      className="navbar-dropdown-item"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <Link key={entry.label} href={entry.href} className="navbar-link">
                {entry.label}
              </Link>
            )
          )}
        </div>

        <div className="navbar-right">
          {/* The product lives on a separate host, so this leaves the
              marketing site entirely. app.payna.com/login is not a route;
              the root handles the redirect to wherever auth lives. */}
          <a href="https://app.payna.com" className="navbar-login">
            Login
          </a>
          <a href="/#book-demo" className="btn-primary navbar-cta">
            Book a demo
          </a>
          <button
            type="button"
            className="navbar-toggle"
            aria-expanded={menuOpen}
            aria-controls="navbar-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'CLOSE' : 'MENU'}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="navbar-menu" id="navbar-menu">
          {NAV_ENTRIES.map((entry) =>
            'links' in entry ? (
              <div key={entry.label} className="navbar-menu-group">
                <span className="navbar-menu-group-label">{entry.label}</span>
                {entry.links.map((link) => (
                  <NavItem
                    key={link.href}
                    link={link}
                    className="navbar-menu-link"
                    onClick={closeMenu}
                  />
                ))}
              </div>
            ) : (
              <Link
                key={entry.label}
                href={entry.href}
                className="navbar-menu-link"
                onClick={closeMenu}
              >
                {entry.label}
              </Link>
            )
          )}
          {/* .navbar-login is hidden below 900px, so the menu carries it. */}
          <a
            href="https://app.payna.com"
            className="navbar-menu-link"
            onClick={closeMenu}
          >
            Login
          </a>
          <a
            href="/#book-demo"
            className="btn-primary navbar-menu-cta"
            onClick={closeMenu}
          >
            Book a demo
          </a>
        </div>
      )}
      </nav>
    </>
  )
}
